import {
  Timestamp,
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  doc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { User } from "firebase/auth";

import { db, isFirebaseConfigured, storage } from "../lib/firebase";

export const COMMUNITY_MAX_IMAGES = 4;
export const COMMUNITY_MAX_IMAGE_BYTES = 2 * 1024 * 1024;
export const COMMUNITY_MAX_TEXT = 2000;

/** Case-insensitive whole-word blocklist for posts & comments. */
export const COMMUNITY_BANNED_WORDS = [
  "fake",
  "shit",
  "fuck",
  "useless",
] as const;

const BANNED_WORD_PATTERN = new RegExp(
  `\\b(?:${COMMUNITY_BANNED_WORDS.map((w) =>
    w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  ).join("|")})\\b`,
  "i",
);

export function containsBannedWords(text: string): boolean {
  return BANNED_WORD_PATTERN.test(text);
}

function assertNoBannedWords(text: string, kind: "post" | "comment") {
  if (!containsBannedWords(text)) return;
  throw new Error(
    kind === "post"
      ? "Your post contains content that isn’t allowed."
      : "Your comment contains content that isn’t allowed.",
  );
}

export type CommunityPost = {
  id: string;
  authorId: string;
  authorName: string;
  authorPhotoURL: string | null;
  text: string;
  imageUrls: string[];
  likeCount: number;
  commentCount: number;
  likedBy: string[];
  createdAt: Date | null;
  isSeed?: boolean;
};

export type CommunityComment = {
  id: string;
  authorId: string;
  authorName: string;
  authorPhotoURL: string | null;
  text: string;
  createdAt: Date | null;
};

function requireDb() {
  if (!isFirebaseConfigured || !db) {
    throw new Error(
      "Firebase is not configured. Add your keys to .env and enable Firestore.",
    );
  }
  return db;
}

function requireStorage() {
  if (!isFirebaseConfigured || !storage) {
    throw new Error(
      "Firebase Storage is not configured. Set REACT_APP_FIREBASE_STORAGE_BUCKET.",
    );
  }
  return storage;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function mapPost(id: string, data: Record<string, unknown>): CommunityPost {
  return {
    id,
    authorId: String(data.authorId ?? ""),
    authorName: String(data.authorName ?? "Collector"),
    authorPhotoURL:
      typeof data.authorPhotoURL === "string" ? data.authorPhotoURL : null,
    text: String(data.text ?? ""),
    imageUrls: Array.isArray(data.imageUrls)
      ? data.imageUrls.filter((u): u is string => typeof u === "string")
      : [],
    likeCount: Number(data.likeCount ?? 0),
    commentCount: Number(data.commentCount ?? 0),
    likedBy: Array.isArray(data.likedBy)
      ? data.likedBy.filter((u): u is string => typeof u === "string")
      : [],
    createdAt: toDate(data.createdAt),
    isSeed: Boolean(data.isSeed),
  };
}

function mapComment(
  id: string,
  data: Record<string, unknown>,
): CommunityComment {
  return {
    id,
    authorId: String(data.authorId ?? ""),
    authorName: String(data.authorName ?? "Collector"),
    authorPhotoURL:
      typeof data.authorPhotoURL === "string" ? data.authorPhotoURL : null,
    text: String(data.text ?? ""),
    createdAt: toDate(data.createdAt),
  };
}

export function subscribePosts(
  onChange: (posts: CommunityPost[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!isFirebaseConfigured || !db) {
    onChange([]);
    return () => undefined;
  }

  const q = query(
    collection(db, "posts"),
    orderBy("createdAt", "desc"),
    limit(50),
  );

  return onSnapshot(
    q,
    (snap) => {
      onChange(snap.docs.map((d) => mapPost(d.id, d.data())));
    },
    (err) => {
      onError?.(err);
      onChange([]);
    },
  );
}

export async function uploadCommunityImages(
  user: User,
  files: File[],
): Promise<string[]> {
  if (files.length > COMMUNITY_MAX_IMAGES) {
    throw new Error(`You can upload up to ${COMMUNITY_MAX_IMAGES} images.`);
  }

  const firebaseStorage = requireStorage();
  const urls: string[] = [];

  for (const file of files) {
    if (!file.type.startsWith("image/") && file.type !== "") {
      throw new Error("Only image files are allowed.");
    }
    if (file.size > COMMUNITY_MAX_IMAGE_BYTES) {
      throw new Error("Each image must be 2MB or smaller.");
    }

    const contentType =
      file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";

    const safeName =
      file.name.replace(/[^\w.-]+/g, "_").slice(0, 80) || "photo.jpg";
    const path = `community/${user.uid}/${Date.now()}_${safeName}`;
    const storageRef = ref(firebaseStorage, path);

    try {
      await uploadBytes(storageRef, file, {
        contentType,
        customMetadata: { uploadedBy: user.uid },
      });
      urls.push(await getDownloadURL(storageRef));
    } catch (err) {
      const code = (err as { code?: string })?.code ?? "";
      const message = err instanceof Error ? err.message : String(err);

      if (
        code.includes("unauthorized") ||
        code.includes("unauthenticated") ||
        /403|forbidden/i.test(message)
      ) {
        throw new Error(
          "Image upload denied. In Firebase Console → Storage → Rules, publish the rules from storage.rules in this repo.",
        );
      }

      if (
        /cors|preflight|ERR_FAILED|network/i.test(message) ||
        code.includes("unknown")
      ) {
        throw new Error(
          "Image upload blocked (Storage CORS / bucket). 1) Enable Storage in Firebase Console. 2) Publish storage.rules. 3) Apply storage-cors.json with: gsutil cors set storage-cors.json gs://YOUR_BUCKET",
        );
      }

      throw new Error(message || "Failed to upload image.");
    }
  }

  return urls;
}

export async function createPost(input: {
  user: User;
  text: string;
  files: File[];
}): Promise<string> {
  const text = input.text.trim();
  if (!text && input.files.length === 0) {
    throw new Error("Write something or add a photo.");
  }
  if (text.length > COMMUNITY_MAX_TEXT) {
    throw new Error(`Posts can be up to ${COMMUNITY_MAX_TEXT} characters.`);
  }
  assertNoBannedWords(text, "post");

  const firestore = requireDb();
  const imageUrls =
    input.files.length > 0
      ? await uploadCommunityImages(input.user, input.files)
      : [];

  try {
    const docRef = await addDoc(collection(firestore, "posts"), {
      authorId: input.user.uid,
      authorName:
        input.user.displayName?.trim() ||
        input.user.email?.split("@")[0] ||
        "Collector",
      authorPhotoURL: input.user.photoURL ?? null,
      text,
      imageUrls,
      likeCount: 0,
      commentCount: 0,
      likedBy: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (err) {
    throw mapFirestoreWriteError(err);
  }
}

function mapFirestoreWriteError(err: unknown): Error {
  const code = (err as { code?: string })?.code ?? "";
  const message = err instanceof Error ? err.message : String(err);

  if (
    code === "permission-denied" ||
    /Missing or insufficient permissions/i.test(message)
  ) {
    return new Error(
      "Firestore permission denied. Open Firebase Console → Firestore Database → Rules, paste firestore.rules from this repo, then click Publish.",
    );
  }

  return err instanceof Error
    ? err
    : new Error(message || "Failed to save post.");
}

export async function toggleLike(
  postId: string,
  userId: string,
  liked: boolean,
) {
  const firestore = requireDb();
  const postRef = doc(firestore, "posts", postId);

  if (liked) {
    await updateDoc(postRef, {
      likedBy: arrayRemove(userId),
      likeCount: increment(-1),
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(postRef, {
      likedBy: arrayUnion(userId),
      likeCount: increment(1),
      updatedAt: serverTimestamp(),
    });
  }
}

export async function addComment(input: {
  postId: string;
  user: User;
  text: string;
}): Promise<void> {
  const text = input.text.trim();
  if (!text) throw new Error("Comment cannot be empty.");
  if (text.length > 500)
    throw new Error("Comments can be up to 500 characters.");
  assertNoBannedWords(text, "comment");

  const firestore = requireDb();
  const postRef = doc(firestore, "posts", input.postId);

  await addDoc(collection(postRef, "comments"), {
    authorId: input.user.uid,
    authorName:
      input.user.displayName?.trim() ||
      input.user.email?.split("@")[0] ||
      "Collector",
    authorPhotoURL: input.user.photoURL ?? null,
    text,
    createdAt: serverTimestamp(),
  });

  await updateDoc(postRef, {
    commentCount: increment(1),
    updatedAt: serverTimestamp(),
  });
}

export async function fetchComments(
  postId: string,
): Promise<CommunityComment[]> {
  const firestore = requireDb();
  const q = query(
    collection(firestore, "posts", postId, "comments"),
    orderBy("createdAt", "asc"),
    limit(100),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapComment(d.id, d.data()));
}

/** Best-effort sync of denormalized author fields on posts & comments. */
export async function syncCommunityAuthorProfile(input: {
  userId: string;
  authorName: string;
  authorPhotoURL: string | null;
}): Promise<void> {
  if (!isFirebaseConfigured || !db) return;

  const firestore = db;
  const patch = {
    authorName: input.authorName,
    authorPhotoURL: input.authorPhotoURL,
  };

  try {
    const postsSnap = await getDocs(
      query(collection(firestore, "posts"), where("authorId", "==", input.userId)),
    );
    await Promise.all(
      postsSnap.docs.map((d) => updateDoc(d.ref, patch).catch(() => undefined)),
    );
  } catch {
    // Ignore if rules/index block; Auth profile is still updated.
  }

  try {
    const commentsSnap = await getDocs(
      query(
        collectionGroup(firestore, "comments"),
        where("authorId", "==", input.userId),
      ),
    );
    await Promise.all(
      commentsSnap.docs.map((d) =>
        updateDoc(d.ref, patch).catch(() => undefined),
      ),
    );
  } catch {
    // collectionGroup may need an index the first time; Auth profile still wins for new posts.
  }
}

export function formatRelativeTime(date: Date | null): string {
  if (!date) return "Just now";
  const diffMs = Date.now() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return date.toLocaleDateString();
}
